import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  Alert,
  useTheme,
  alpha,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Paper,
  Card,
  CardContent,
  Fade,
  Tooltip,
  Popper,
  ClickAwayListener
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberedIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Assignment as AssignmentIcon,
  TableChart as TableIcon,
  HorizontalRule as HRuleIcon,
  FormatQuote as QuoteIcon,
  Title as HeaderIcon,
  Visibility as PreviewIcon,
  VisibilityOff as EditIcon,
  ViewColumn as ColumnsIcon,
  CheckBox as ChecklistIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as DangerIcon,
  CheckCircle as SuccessIcon,
  List as TOCIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../../../supabaseClient';

const MarkdownToolbar = ({ onInsert, isUploading }) => {
  const theme = useTheme();

  const tools = [
    { icon: <BoldIcon />, action: () => onInsert('**', '**'), tooltip: 'Bold (Ctrl+B)', shortcut: 'Ctrl+B' },
    { icon: <ItalicIcon />, action: () => onInsert('*', '*'), tooltip: 'Italic (Ctrl+I)', shortcut: 'Ctrl+I' },
    { icon: <BulletIcon />, action: () => onInsert('\n- ', ''), tooltip: 'Bullet List (Ctrl+Shift+8)', shortcut: 'Ctrl+Shift+8' },
    { icon: <NumberedIcon />, action: () => onInsert('\n1. ', ''), tooltip: 'Numbered List (Ctrl+Shift+7)', shortcut: 'Ctrl+Shift+7' },
    { icon: <CodeIcon />, action: () => onInsert('`', '`'), tooltip: 'Code (Ctrl+`)', shortcut: 'Ctrl+`' },
    { icon: <LinkIcon />, action: () => onInsert('[', '](url)'), tooltip: 'Link (Ctrl+K)', shortcut: 'Ctrl+K' },
    { icon: <TableIcon />, action: () => onInsert('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', ''), tooltip: 'Table (Ctrl+T)', shortcut: 'Ctrl+T' },
    { icon: <HRuleIcon />, action: () => onInsert('\n---\n', ''), tooltip: 'Horizontal Rule (Ctrl+H)', shortcut: 'Ctrl+H' }
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      p: 2, 
      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
      background: alpha(theme.palette.grey[50], 0.8)
    }}>
      {tools.map((tool, index) => (
        <Tooltip
          key={index}
          title={tool.tooltip}
          placement="top"
          arrow
        >
          <IconButton
          size="small"
          onClick={tool.action}
          disabled={tool.disabled}
          sx={{
            border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            borderRadius: 2,
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main
            },
            '&:disabled': {
              opacity: 0.5,
                backgroundColor: theme.palette.grey[100]
            },
              '&:active': {
                transform: 'scale(0.95)'
              },
              transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease'
          }}
        >
          {tool.icon}
        </IconButton>
        </Tooltip>
      ))}
    </Box>
  );
};

const ShiftEditor = ({ 
  open, 
  onClose, 
  task,
  shift, // Keep for backward compatibility
  employees, 
  departments, 
  selectedDate, 
  selectedEmployee, 
  onSave, 
  onDelete,
  timeFormat = '24hour',
  canEdit = true,
  canDelete = true
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    employee_id: '',
    department_id: '',
    shift_date: null,
    start_time: null,
    end_time: null,
    job_position: '',
    role_documentation: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  // Markdown editor state
  const [roleDocRef, setRoleDocRef] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editorFocused, setEditorFocused] = useState(false);
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [commandMenuAnchor, setCommandMenuAnchor] = useState(null);
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Employee selection dialog states
  const [employeeSelectionDialogOpen, setEmployeeSelectionDialogOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [dialogSelectedEmployee, setDialogSelectedEmployee] = useState(null);

  // Department selection dialog states  
  const [departmentSelectionDialogOpen, setDepartmentSelectionDialogOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [dialogSelectedDepartment, setDialogSelectedDepartment] = useState(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      const currentTask = task || shift; // Use task if provided, otherwise fall back to shift
      if (currentTask) {
        // Editing existing task
        setFormData({
          employee_id: currentTask.employee_id || '',
          department_id: currentTask.department_id || '',
          shift_date: currentTask.shift_date ? dayjs(currentTask.shift_date) : null,
          start_time: currentTask.start_time ? dayjs(`2000-01-01T${currentTask.start_time}`) : null,
          end_time: currentTask.end_time ? dayjs(`2000-01-01T${currentTask.end_time}`) : null,
          job_position: currentTask.job_position || '',
          role_documentation: currentTask.role_documentation || '',
          notes: currentTask.notes || ''
        });
      } else {
        // Creating new task - use actual clicked time if available
        let defaultStartTime, defaultEndTime;
        
        if (selectedDate && selectedDate instanceof Date) {
          // Use the actual clicked time from the time slot
          defaultStartTime = dayjs(selectedDate);
          defaultEndTime = dayjs(selectedDate).add(8, 'hour'); // 8 hour task by default
        } else {
          // Fallback to 9 AM if no specific time
          defaultStartTime = dayjs().hour(9).minute(0);
          defaultEndTime = dayjs().hour(17).minute(0);
        }
        
        setFormData({
          employee_id: selectedEmployee?.id || '',
          department_id: selectedEmployee?.department_id || '',
          shift_date: selectedDate ? dayjs(selectedDate) : dayjs(),
          start_time: defaultStartTime,
          end_time: defaultEndTime,
          job_position: selectedEmployee?.job_position || '',
          role_documentation: '',
          notes: ''
        });
      }
      setErrors({});
    }
  }, [open, task, shift, selectedDate, selectedEmployee]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Check for conflicts when key fields change
    if (['employee_id', 'shift_date', 'start_time', 'end_time'].includes(field)) {
      checkShiftConflicts();
    }

    // Handle slash command menu for role_documentation
    if (field === 'role_documentation') {
      const cursorPos = roleDocRef?.selectionStart || value.length;
      const beforeCursor = value.substring(0, cursorPos);
      const lines = beforeCursor.split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Show menu when line starts with / (even just "/")
      if (currentLine.startsWith('/')) {
        const searchTerm = currentLine.substring(1).toLowerCase(); // Remove the /
        let filtered;
        
        if (searchTerm === '') {
          // Show all commands when just "/" is typed
          filtered = commands;
        } else {
          // Filter commands based on search term - more flexible matching
          filtered = commands.filter(cmd => {
            const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
            const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
            const descMatch = cmd.description.toLowerCase().includes(searchTerm);
            
            // Special keyword matching
                      const keywordMatches = {
                        'head': cmd.name.includes('heading'),
                        'header': cmd.name.includes('heading'),
                        'h': cmd.name.includes('heading'),
                        'list': cmd.name.includes('bullet') || cmd.name.includes('number') || cmd.name.includes('checklist'),
                        'table': cmd.name.includes('table') || cmd.name.includes('column'),
                        'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
                        'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
                        'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
                      };
            
            const keywordMatch = keywordMatches[searchTerm] || false;
            
            return nameMatch || aliasMatch || descMatch || keywordMatch;
          });
        }
        
        if (filtered.length > 0) {
          setFilteredCommands(filtered);
          setSelectedCommandIndex(0);
          setCommandMenuOpen(true);
          
          // Position menu at cursor line
          setTimeout(() => {
            if (roleDocRef) {
              // Get the textarea element
              const textarea = roleDocRef;
              const rect = textarea.getBoundingClientRect();
              
              // Create a canvas to measure text width accurately
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const computedStyle = window.getComputedStyle(textarea);
              ctx.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;
              
              // Get text before cursor and split into lines
              const textBeforeCursor = value.substring(0, cursorPos);
              const lines = textBeforeCursor.split('\n');
              const currentLineText = lines[lines.length - 1];
              const lineNumber = lines.length - 1;
              
              // Calculate line height
              const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
              const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
              const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
              
              // Calculate cursor position
              const lineY = paddingTop + (lineNumber * lineHeight) - textarea.scrollTop;
              const lineX = paddingLeft + ctx.measureText(currentLineText).width;
              
              // Position menu
              setCommandMenuAnchor({
                getBoundingClientRect: () => ({
                  left: rect.left + lineX + 5,
                  top: rect.top + lineY + lineHeight,
                  right: rect.left + lineX + 5,
                  bottom: rect.top + lineY + lineHeight,
                  width: 0,
                  height: 0
                })
              });
            }
          }, 10);
        } else {
          setCommandMenuOpen(false);
        }
      } else {
        setCommandMenuOpen(false);
      }
    }
  };

  // Check for shift conflicts
  const checkShiftConflicts = async () => {
    if (!formData.employee_id || !formData.shift_date || !formData.start_time || !formData.end_time) {
      setConflicts([]);
      return;
    }

    setCheckingConflicts(true);
    try {
      const { data, error } = await supabase.rpc('check_shift_conflicts', {
        p_employee_id: formData.employee_id,
        p_shift_date: formData.shift_date.format('YYYY-MM-DD'),
        p_start_time: formData.start_time.format('HH:mm:ss'),
        p_end_time: formData.end_time.format('HH:mm:ss'),
        p_exclude_shift_id: (task || shift)?.id || null
      });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setConflicts(data[0].conflicting_shifts || []);
      } else {
        setConflicts([]);
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
      setConflicts([]);
    } finally {
      setCheckingConflicts(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employee_id) {
      newErrors.employee_id = 'Employee is required';
    }
    
    if (!formData.shift_date) {
      newErrors.shift_date = 'Date is required';
    }
    
    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }
    
    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }
    
    if (formData.start_time && formData.end_time) {
      if (formData.end_time.isBefore(formData.start_time)) {
        newErrors.end_time = 'End time must be after start time';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!canEdit) return;
    
    if (!validateForm()) {
      return;
    }
    
    const taskData = {
      employee_id: formData.employee_id,
      department_id: formData.department_id,
      shift_date: formData.shift_date.format('YYYY-MM-DD'),
      start_time: formData.start_time.format('HH:mm:ss'),
      end_time: formData.end_time.format('HH:mm:ss'),
      job_position: formData.job_position,
      role_documentation: formData.role_documentation,
      notes: formData.notes
    };
    
    onSave(taskData);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!canDelete) return;
    
    const currentTask = task || shift;
    if (currentTask && currentTask.id) {
      onDelete(currentTask.id);
      setDeleteDialogOpen(false);
      onClose(); // Close the task editor after deletion
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const calculateTaskDuration = () => {
    if (formData.start_time && formData.end_time) {
      const duration = formData.end_time.diff(formData.start_time, 'minutes');
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}h ${minutes}m`;
    }
    return '0h 0m';
  };

  // Employee dialog handlers
  const handleOpenEmployeeSelectionDialog = () => {
    setEmployeeSelectionDialogOpen(true);
    setEmployeeSearchTerm('');
    setDialogSelectedEmployee(null);
  };

  const handleCloseEmployeeSelectionDialog = () => {
    setEmployeeSelectionDialogOpen(false);
    setEmployeeSearchTerm('');
    setDialogSelectedEmployee(null);
  };

  const handleDialogEmployeeSelect = (employee) => {
    setDialogSelectedEmployee(employee);
  };

  const handleConfirmEmployeeSelection = () => {
    if (dialogSelectedEmployee) {
      handleInputChange('employee_id', dialogSelectedEmployee.id);
      handleInputChange('department_id', dialogSelectedEmployee.department_id || '');
      handleInputChange('job_position', dialogSelectedEmployee.job_position || '');
      setEmployeeSelectionDialogOpen(false);
      setEmployeeSearchTerm('');
      setDialogSelectedEmployee(null);
    }
  };

  const getFilteredEmployeesForSelection = () => {
    if (!employeeSearchTerm) return employees || [];
    
    const searchLower = employeeSearchTerm.toLowerCase();
    return (employees || []).filter(employee => 
      employee.first_name.toLowerCase().includes(searchLower) ||
      employee.last_name.toLowerCase().includes(searchLower) ||
      employee.email?.toLowerCase().includes(searchLower)
    );
  };

  // Department dialog handlers
  const handleOpenDepartmentSelectionDialog = () => {
    setDepartmentSelectionDialogOpen(true);
    setDepartmentSearchTerm('');
    setDialogSelectedDepartment(null);
  };

  const handleCloseDepartmentSelectionDialog = () => {
    setDepartmentSelectionDialogOpen(false);
    setDepartmentSearchTerm('');
    setDialogSelectedDepartment(null);
  };

  const handleDialogDepartmentSelect = (department) => {
    setDialogSelectedDepartment(department);
  };

  const handleConfirmDepartmentSelection = () => {
    if (dialogSelectedDepartment) {
      handleInputChange('department_id', dialogSelectedDepartment.id);
      setDepartmentSelectionDialogOpen(false);
      setDepartmentSearchTerm('');
      setDialogSelectedDepartment(null);
    }
  };

  const getFilteredDepartmentsForSelection = () => {
    if (!departmentSearchTerm) return departments || [];
    
    const searchLower = departmentSearchTerm.toLowerCase();
    return (departments || []).filter(department => 
      department.name.toLowerCase().includes(searchLower)
    );
  };

  const selectedEmployeeData = employees.find(emp => emp.id === formData.employee_id);
  const selectedDepartmentData = departments.find(dept => dept.id === formData.department_id);

  // Command sections for slash commands
  const commandSections = [
    {
      title: 'FORMAT',
      commands: [
        { name: '/heading1', alias: '/h1', icon: <HeaderIcon />, description: 'Big section heading', action: () => insertMarkdown('\n# ', '') },
        { name: '/heading2', alias: '/h2', icon: <HeaderIcon />, description: 'Medium section heading', action: () => insertMarkdown('\n## ', '') },
        { name: '/heading3', alias: '/h3', icon: <HeaderIcon />, description: 'Small section heading', action: () => insertMarkdown('\n### ', '') },
        { name: '/text', alias: '/p', icon: <AssignmentIcon />, description: 'Paragraph block', action: () => insertMarkdown('\n', '') },
        { name: '/quote', alias: '/q', icon: <QuoteIcon />, description: 'Add a blockquote section', action: () => insertMarkdown('\n> ', '') },
        { name: '/code', alias: '/c', icon: <CodeIcon />, description: 'Add a code section', action: () => insertMarkdown('\n```\n', '\n```\n') },
        { name: '/bold', alias: '/b', icon: <BoldIcon />, description: 'Bold text', action: () => insertMarkdown('**', '**') },
        { name: '/italic', alias: '/i', icon: <ItalicIcon />, description: 'Italic text', action: () => insertMarkdown('*', '*') }
      ]
    },
    {
      title: 'STRUCTURE',
      commands: [
        { name: '/separator', alias: '/hr', icon: <HRuleIcon />, description: 'Insert a horizontal rule separator', action: () => insertMarkdown('\n---\n', '') },
        { name: '/bullet', alias: '/ul', icon: <BulletIcon />, description: 'Create a simple bulleted list', action: () => insertMarkdown('\n- ', '') },
        { name: '/number', alias: '/ol', icon: <NumberedIcon />, description: 'Create a list with numbering', action: () => insertMarkdown('\n1. ', '') },
        { name: '/checklist', alias: '/check', icon: <ChecklistIcon />, description: 'Track tasks with a checklist', action: () => insertMarkdown('\n- [ ] ', '') },
        { name: '/2columns', alias: '/2col', icon: <ColumnsIcon />, description: 'Convert into 2 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 |\n|----------|----------|\n| ', ' | |\n') },
        { name: '/3columns', alias: '/3col', icon: <ColumnsIcon />, description: 'Convert into 3 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', ' | | |\n') },
        { name: '/table', alias: '/t', icon: <TableIcon />, description: 'Insert a table', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '') },
        { name: '/toc', alias: '/contents', icon: <TOCIcon />, description: 'Table of Contents', action: () => generateTableOfContents() }
      ]
    },
    {
      title: 'BANNER',
      commands: [
        { name: '/info', alias: '/i', icon: <InfoIcon />, description: 'Insert an info banner', action: () => insertMarkdown('\n> ℹ️ **Info:** ', '\n') },
        { name: '/success', alias: '/s', icon: <SuccessIcon />, description: 'Insert a success banner', action: () => insertMarkdown('\n> ✅ **Success:** ', '\n') },
        { name: '/warning', alias: '/w', icon: <WarningIcon />, description: 'Insert a warning banner', action: () => insertMarkdown('\n> ⚠️ **Warning:** ', '\n') },
        { name: '/danger', alias: '/d', icon: <DangerIcon />, description: 'Insert a danger banner', action: () => insertMarkdown('\n> ❌ **Danger:** ', '\n') }
      ]
    },
    {
      title: 'NAVIGATION',
      commands: [
        { name: '/link', alias: '/l', icon: <LinkIcon />, description: 'Add a link', action: () => insertMarkdown('[', '](url)') }
      ]
    }
  ];

  // Flatten commands for filtering
  const commands = commandSections.flatMap(section => section.commands);

  // Markdown editor functions
  const insertMarkdown = (before, after) => {
    if (roleDocRef) {
      const start = roleDocRef.selectionStart || 0;
      const end = roleDocRef.selectionEnd || 0;
      const selectedText = formData.role_documentation.substring(start, end);
      const newText = formData.role_documentation.substring(0, start) + 
                     before + selectedText + after + 
                     formData.role_documentation.substring(end);
      
      setFormData(prev => ({ ...prev, role_documentation: newText }));
      
      setTimeout(() => {
        roleDocRef.focus();
        roleDocRef.setSelectionRange(
          start + before.length,
          start + before.length + selectedText.length
        );
      }, 0);
    } else {
      const newText = formData.role_documentation + before + after;
      setFormData(prev => ({ ...prev, role_documentation: newText }));
    }
  };

  const generateTableOfContents = () => {
    // Get current text
    const currentText = formData.role_documentation;
    const lines = currentText.split('\n');
    const headers = [];
    
    // Find all headers in the document
    lines.forEach((line, index) => {
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const title = headerMatch[2].trim();
        const anchor = title.toLowerCase()
          .replace(/[^\w\s-]/g, '') // Remove special chars
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        
        headers.push({ level, title, anchor });
      }
    });
    
    // Generate TOC markdown
    let tocMarkdown;
    
    if (headers.length === 0) {
      // No headers found, insert a sample TOC
      tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
    } else {
      // Generate actual TOC
      tocMarkdown = '## Table of Contents\n\n';
      
      headers.forEach(header => {
        const indent = '  '.repeat(Math.max(0, header.level - 1));
        tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
      });
    }
    
    // Get cursor position and insert the TOC
    const textarea = roleDocRef;
    const cursorPos = textarea?.selectionStart || 0;
    const beforeCursor = currentText.substring(0, cursorPos);
    const afterCursor = currentText.substring(cursorPos);
    
    const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
    setFormData(prev => ({ ...prev, role_documentation: newText }));
    
    // Position cursor after the TOC
    setTimeout(() => {
      const newCursorPos = cursorPos + tocMarkdown.length + 2;
      textarea?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const executeCommand = (command) => {
    const textarea = roleDocRef;
    const value = formData.role_documentation;
    const cursorPos = textarea?.selectionStart || value.length;
    const beforeCursor = value.substring(0, cursorPos);
    const afterCursor = value.substring(cursorPos);
    
    // Find the start of the current line
    const lineStart = beforeCursor.lastIndexOf('\n') + 1;
    const currentLine = beforeCursor.substring(lineStart);
    
    // Remove the entire slash command (including the /) from current line
    const slashIndex = currentLine.lastIndexOf('/');
    if (slashIndex !== -1) {
      const beforeSlash = beforeCursor.substring(0, lineStart + slashIndex);
      const cleanedText = beforeSlash + afterCursor;
      const newCursorPos = lineStart + slashIndex;
      
      setCommandMenuOpen(false);
      
      // Execute command directly with cleaned text
      setTimeout(() => {
        // Manually execute the markdown insertion with cleaned text
        const insertAt = newCursorPos;
        let finalText;
        
        if (command.name === '/bold') {
          finalText = cleanedText.substring(0, insertAt) + '**bold text**' + cleanedText.substring(insertAt);
          setFormData(prev => ({ ...prev, role_documentation: finalText }));
          setTimeout(() => {
            textarea?.setSelectionRange(insertAt + 2, insertAt + 11); // Select "bold text"
          }, 0);
        } else if (command.name === '/italic') {
          finalText = cleanedText.substring(0, insertAt) + '*italic text*' + cleanedText.substring(insertAt);
          setFormData(prev => ({ ...prev, role_documentation: finalText }));
          setTimeout(() => {
            textarea?.setSelectionRange(insertAt + 1, insertAt + 12); // Select "italic text"
          }, 0);
        } else {
          // For other commands, extract the before/after parts from the action
          const commandActions = {
            '/heading1': { before: '\n# ', after: '' },
            '/heading2': { before: '\n## ', after: '' },
            '/heading3': { before: '\n### ', after: '' },
            '/text': { before: '\n', after: '' },
            '/quote': { before: '\n> ', after: '' },
            '/code': { before: '\n```\n', after: '\n```\n' },
            '/separator': { before: '\n---\n', after: '' },
            '/bullet': { before: '\n- ', after: '' },
            '/number': { before: '\n1. ', after: '' },
            '/checklist': { before: '\n- [ ] ', after: '' },
            '/2columns': { before: '\n| Column 1 | Column 2 |\n|----------|----------|\n| ', after: ' | |\n' },
            '/3columns': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', after: ' | | |\n' },
            '/table': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', after: '' },
            '/info': { before: '\n> ℹ️ **Info:** ', after: '\n' },
            '/success': { before: '\n> ✅ **Success:** ', after: '\n' },
            '/warning': { before: '\n> ⚠️ **Warning:** ', after: '\n' },
            '/danger': { before: '\n> ❌ **Danger:** ', after: '\n' },
            '/link': { before: '[', after: '](url)' },
            '/toc': null // Special handling for TOC
          };
          
          const action = commandActions[command.name];
          if (action === null && command.name === '/toc') {
            // Special handling for Table of Contents - use the cleaned text
            setTimeout(() => {
              // Set the cleaned text first (without /toc)
              setFormData(prev => ({ ...prev, role_documentation: cleanedText }));
              
              // Then generate TOC with the cleaned text
              setTimeout(() => {
                if (textarea) {
                  textarea.focus();
                  textarea.setSelectionRange(insertAt, insertAt);
                }
                
                // Generate TOC using the cleaned text
                const currentText = cleanedText;
                const lines = currentText.split('\n');
                const headers = [];
                
                // Find all headers in the document
                lines.forEach((line, index) => {
                  const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
                  if (headerMatch) {
                    const level = headerMatch[1].length;
                    const title = headerMatch[2].trim();
                    const anchor = title.toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    
                    headers.push({ level, title, anchor });
                  }
                });
                
                // Generate TOC markdown
                let tocMarkdown;
                
                if (headers.length === 0) {
                  tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
                } else {
                  tocMarkdown = '## Table of Contents\n\n';
                  headers.forEach(header => {
                    const indent = '  '.repeat(Math.max(0, header.level - 1));
                    tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
                  });
                }
                
                // Insert TOC at cursor position
                const beforeCursor = currentText.substring(0, insertAt);
                const afterCursor = currentText.substring(insertAt);
                const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
                
                setFormData(prev => ({ ...prev, role_documentation: newText }));
                
                // Position cursor after the TOC
                setTimeout(() => {
                  const newCursorPos = insertAt + tocMarkdown.length + 2;
                  textarea?.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
              }, 10);
            }, 0);
          } else if (action) {
            finalText = cleanedText.substring(0, insertAt) + action.before + action.after + cleanedText.substring(insertAt);
            setFormData(prev => ({ ...prev, role_documentation: finalText }));
            setTimeout(() => {
              textarea?.setSelectionRange(insertAt + action.before.length, insertAt + action.before.length);
            }, 0);
          }
        }
      }, 0);
    }
  };

  const handleRoleDocumentationKeyDown = (e) => {
    // Handle command menu navigation
    if (commandMenuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
        return;
      }
      
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredCommands[selectedCommandIndex]) {
          executeCommand(filteredCommands[selectedCommandIndex]);
        }
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        setCommandMenuOpen(false);
        return;
      }
    }

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertMarkdown('**', '**');
          return;
        case 'i':
          e.preventDefault();
          insertMarkdown('*', '*');
          return;
        case 'k':
          e.preventDefault();
          insertMarkdown('[', '](url)');
          return;
        case '`':
          e.preventDefault();
          insertMarkdown('`', '`');
          return;
        case 't':
          e.preventDefault();
          insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '');
          return;
        case 'h':
          e.preventDefault();
          insertMarkdown('\n---\n', '');
          return;
      }
      
      // Handle Ctrl+Shift combinations
      if (e.shiftKey) {
        switch (e.key) {
          case '*': // Ctrl+Shift+8
            e.preventDefault();
            insertMarkdown('\n- ', '');
            return;
          case '&': // Ctrl+Shift+7
            e.preventDefault();
            insertMarkdown('\n1. ', '');
            return;
        }
      }
    }

    // Handle Enter key for list continuation
    if (e.key === 'Enter' && !e.shiftKey) {
      const textarea = e.target;
      const value = textarea.value;
      const cursorPos = textarea.selectionStart;
      
      // Get the current line
      const lines = value.substring(0, cursorPos).split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Check for bullet points
      const bulletMatch = currentLine.match(/^(\s*)-\s/);
      if (bulletMatch) {
        e.preventDefault();
        const indent = bulletMatch[1];
        const newText = value.substring(0, cursorPos) + '\n' + indent + '- ' + value.substring(cursorPos);
        setFormData(prev => ({ ...prev, role_documentation: newText }));
        
        setTimeout(() => {
          const newCursorPos = cursorPos + indent.length + 3;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
      
      // Check for numbered lists
      const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s/);
      if (numberMatch) {
        e.preventDefault();
        const indent = numberMatch[1];
        const nextNumber = parseInt(numberMatch[2]) + 1;
        const newText = value.substring(0, cursorPos) + '\n' + indent + nextNumber + '. ' + value.substring(cursorPos);
        setFormData(prev => ({ ...prev, role_documentation: newText }));
        
        setTimeout(() => {
          const newCursorPos = cursorPos + indent.length + nextNumber.toString().length + 3;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: `0 8px 32px ${alpha(theme.palette.grey[900], 0.15)}`,
            height: '90vh',
            maxHeight: '100vh',
            width: '90vw'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500, 
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          py: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <ScheduleIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            {(task || shift) ? 'Edit Task' : 'Create New Task'}
          </Box>
          
          <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, pb: 3, mt: 4 }}>
          <Grid container spacing={3}>
            {/* ROW 1: Employee, Department */}
            <Grid item xs={12} md={6} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                  Employee
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenEmployeeSelectionDialog}
                  disabled={!canEdit}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1,
                    px: 2,
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    border: `1px solid rgba(0,0,0,0.1)`,
                    color: formData.employee_id ? 'text.primary' : 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      border: `1px solid rgba(0,0,0,0.2)`,
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                >
                  {formData.employee_id ? 
                    `${selectedEmployeeData?.first_name || ''} ${selectedEmployeeData?.last_name || ''}` :
                    'Select Employee'
                  }
                </Button>
                {errors.employee_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.employee_id}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                  Department
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenDepartmentSelectionDialog}
                  disabled={!canEdit}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1,
                    px: 2,
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    border: `1px solid rgba(0,0,0,0.1)`,
                    color: formData.department_id ? 'text.primary' : 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      border: `1px solid rgba(0,0,0,0.2)`,
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                >
                  {formData.department_id ? 
                    selectedDepartmentData?.name || 'Select Department' :
                    'Select Department'
                  }
                </Button>
              </Box>
            </Grid>

            {/* ROW 2: Position */}
            <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Position/Role"
                        value={formData.job_position}
                        onChange={(e) => handleInputChange('job_position', e.target.value)}
                        placeholder="e.g., Server, Cashier, Manager"
                        disabled={!canEdit}
                      />
            </Grid>

            {/* ROW 3: Date, Start Time, End Time */}
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Date"
                value={formData.shift_date}
                onChange={(newValue) => handleInputChange('shift_date', newValue)}
                disabled={!canEdit}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.shift_date,
                    helperText: errors.shift_date
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TimePicker
                label="Start Time"
                value={formData.start_time}
                onChange={(newValue) => handleInputChange('start_time', newValue)}
                ampm={timeFormat !== '24hour'}
                disabled={!canEdit}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.start_time,
                    helperText: errors.start_time
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TimePicker
                label="End Time"
                value={formData.end_time}
                onChange={(newValue) => handleInputChange('end_time', newValue)}
                ampm={timeFormat !== '24hour'}
                disabled={!canEdit}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.end_time,
                    helperText: errors.end_time
                  }
                }}
              />
            </Grid>


            {/* ROW 4: Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                multiline
                rows={3}
                placeholder="Additional notes or instructions for this task..."
                disabled={!canEdit}
              />
            </Grid>

            {/* Task Summary */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {calculateTaskDuration()}
                  </Typography>
                  {selectedEmployeeData && (
                    <Typography variant="body2">
                      <strong>Employee:</strong> {selectedEmployeeData.first_name} {selectedEmployeeData.last_name}
                    </Typography>
                  )}
                </Box>
                {checkingConflicts && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    Checking for conflicts...
                  </Typography>
                )}
              </Alert>
            </Grid>

            {/* ROLE SPECIFICS SECTION */}
            <Grid item xs={12} sx={{ mt: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                Role Specifics & Responsibilities
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: 'white'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 3, 
                  pb: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AssignmentIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500, 
                      color: 'primary.main',
                      fontSize: '1.2rem',
                      letterSpacing: '0.5px'
                    }}>
                      Primary Responsibilities
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setPreviewMode(!previewMode)}
                    size="medium"
                    sx={{
                      border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      borderRadius: 2,
                      backgroundColor: 'white',
                      p: 1.5,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                  >
                    {previewMode ? <EditIcon sx={{ fontSize: 20 }} /> : <PreviewIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Box>

                {!previewMode && (
                  <MarkdownToolbar onInsert={insertMarkdown} isUploading={isUploading} />
                )}
                {previewMode && (
                  <Box sx={{ height: '73px' }} />
                )}

                {/* Markdown Editor Implementation */}
                <Box sx={{ 
                  position: 'relative', 
                  height: '1000px',
                  display: 'flex',
                  overflow: 'hidden'
                }}>
                  {/* Left Side - Markdown Input */}
                  <Box sx={{ 
                    width: previewMode ? '0%' : (formData.role_documentation ? '50%' : '100%'),
                    height: '100%',
                    transition: 'width 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: previewMode ? 0 : 1
                  }}>
                    <TextField
                      fullWidth
                      multiline
                      disabled={isUploading || !canEdit}
                      value={formData.role_documentation}
                      onChange={(e) => handleInputChange('role_documentation', e.target.value)}
                      onKeyDown={handleRoleDocumentationKeyDown}
                      onFocus={() => {
                        setEditorFocused(true);
                        setCursorPosition(roleDocRef?.selectionStart || 0);
                      }}
                      onBlur={() => setEditorFocused(false)}
                      onClick={() => setCursorPosition(roleDocRef?.selectionStart || 0)}
                      onKeyUp={() => setCursorPosition(roleDocRef?.selectionStart || 0)}
                      inputRef={setRoleDocRef}
                      sx={{
                        height: '100%',
                        '& .MuiInputBase-root': {
                          height: '100%',
                          alignItems: 'stretch',
                          borderRadius: 0
                        },
                        '& .MuiInputBase-inputMultiline': {
                          height: '100% !important',
                          resize: 'none',
                          overflow: 'auto !important'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        }
                      }}
                      InputProps={{
                        sx: {
                          fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                          fontSize: '0.9rem',
                          lineHeight: 1.6,
                          height: '100%',
                          padding: 2,
                          borderRadius: 0
                        }
                      }}
                    />
                    
                    {/* Slash Command Hint Overlay */}
                    {(() => {
                      if (!roleDocRef || isUploading || !document.activeElement || document.activeElement !== roleDocRef) {
                        return null;
                      }
                      
                      const cursorPos = cursorPosition;
                      const beforeCursor = formData.role_documentation.substring(0, cursorPos);
                      const lines = beforeCursor.split('\n');
                      const currentLine = lines[lines.length - 1];
                      const lineNumber = lines.length - 1;
                      
                      // Show hint only on empty lines
                      if (currentLine.trim() !== '') {
                        return null;
                      }
                      
                      // Calculate cursor position more accurately
                      const computedStyle = window.getComputedStyle(roleDocRef);
                      const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
                      const paddingTop = parseFloat(computedStyle.paddingTop) || 16;
                      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 16;
                      
                      // Position at cursor line with proper offset
                      const lineY = paddingTop + (lineNumber * lineHeight) - (roleDocRef.scrollTop || 0) + 2;
                      const lineX = paddingLeft + 2;
                      
                      return (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: `${lineY}px`,
                            left: `${lineX}px`,
                            pointerEvents: 'none',
                            color: alpha(theme.palette.text.secondary, 0.4),
                            fontSize: '0.9rem',
                            fontFamily: computedStyle.fontFamily,
                            lineHeight: `${lineHeight}px`,
                            zIndex: 1,
                            userSelect: 'none',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Type '/' for commands
                        </Box>
                      );
                    })()}
                  </Box>
                  
                  {/* Right Side - Live Preview */}
                  {(formData.role_documentation || previewMode) && (
                    <Box sx={{ 
                      width: previewMode ? '100%' : '50%',
                      height: '100%',
                      borderLeft: previewMode ? 'none' : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      overflow: 'auto',
                      backgroundColor: alpha(theme.palette.grey[50], 0.3),
                      p: 2,
                      transition: 'width 0.3s ease, border-left 0.3s ease'
                    }}>
                      <Box sx={{
                        '& ul': {
                          listStyle: 'none !important',
                          paddingLeft: '0 !important',
                          marginLeft: '0 !important'
                        },
                        '& ul li': {
                          listStyle: 'none !important',
                          listStyleType: 'none !important',
                          '&::marker': {
                            display: 'none !important'
                          },
                          '&::before': {
                            display: 'none !important'
                          }
                        },
                        '& ol': {
                          listStyle: 'decimal !important',
                          paddingLeft: '20px !important',
                          marginLeft: '0 !important'
                        },
                        '& ol li': {
                          listStyle: 'decimal !important',
                          listStyleType: 'decimal !important',
                          display: 'list-item !important',
                          '&::marker': {
                            display: 'block !important'
                          }
                        }
                      }}>
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                          h1: ({ children }) => (
                            <h1 style={{
                              fontSize: '1.5rem',
                              fontWeight: 500,
                              margin: '24px 0 16px 0',
                              color: theme.palette.text.primary,
                              borderBottom: `2px solid ${theme.palette.primary.main}`,
                              paddingBottom: '8px'
                            }}>
                              {children}
                            </h1>
                          ),
                          h2: ({ children }) => (
                            <h2 style={{
                              fontSize: '1.25rem',
                              fontWeight: 500,
                              margin: '20px 0 12px 0',
                              color: theme.palette.text.primary,
                              borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                              paddingBottom: '6px'
                            }}>
                              {children}
                            </h2>
                          ),
                          h3: ({ children }) => (
                            <h3 style={{
                              fontSize: '1.1rem',
                              fontWeight: 500,
                              margin: '16px 0 8px 0',
                              color: theme.palette.text.primary
                            }}>
                              {children}
                            </h3>
                          ),
                          p: ({ children }) => (
                            <p style={{
                              margin: '8px 0',
                              lineHeight: '1.6',
                              color: theme.palette.text.primary
                            }}>
                              {children}
                            </p>
                          ),
                              ul: ({ children, ...props }) => {
                                return (
                                  <ul style={{
                                    margin: '8px 0',
                                    paddingLeft: '0px',
                                    color: theme.palette.text.primary,
                                    listStyle: 'none'
                                  }}>
                                    {children}
                                  </ul>
                                );
                              },
                          ol: ({ children }) => (
                            <ol style={{
                              margin: '8px 0',
                              paddingLeft: '20px',
                              color: theme.palette.text.primary,
                              listStyle: 'decimal',
                              listStyleType: 'decimal'
                            }}>
                              {children}
                            </ol>
                          ),
                              li: ({ children, ...props }) => {
                                const childrenArray = React.Children.toArray(children);
                                const firstChild = childrenArray[0];
                                
                                // Check if this is a checkbox item
                                if (typeof firstChild === 'string') {
                                  if (firstChild.startsWith('☐ ')) {
                                    const taskText = firstChild.substring(2);
                                    const handleCheckboxChange = () => {
                                      const currentText = formData.role_documentation;
                                      const newText = currentText.replace(`- [ ] ${taskText}`, `- [x] ${taskText}`);
                                      handleInputChange('role_documentation', newText);
                                    };
                                    
                                    return (
                                      <li style={{
                                        margin: '4px 0',
                                        lineHeight: '1.5',
                                        color: theme.palette.text.primary,
                                        listStyleType: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start'
                                      }}>
                                        <Box
                                          onClick={handleCheckboxChange}
                                          sx={{
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid',
                                            borderColor: theme.palette.grey[400],
                                            borderRadius: '4px',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '8px',
                                            marginTop: '2px',
                                            '&:hover': {
                                              borderColor: theme.palette.primary.main,
                                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                              transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s ease'
                                          }}
                                        />
                                        <span>{taskText}{childrenArray.slice(1)}</span>
                                      </li>
                                    );
                                  }
                                  if (firstChild.startsWith('☑ ')) {
                                    const taskText = firstChild.substring(2);
                                    const handleCheckboxChange = () => {
                                      const currentText = formData.role_documentation;
                                      const newText = currentText.replace(`- [x] ${taskText}`, `- [ ] ${taskText}`);
                                      handleInputChange('role_documentation', newText);
                                    };
                                    
                                    return (
                                      <li style={{
                                        margin: '4px 0',
                                        lineHeight: '1.5',
                                        color: theme.palette.text.primary,
                                        listStyleType: 'none',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        textDecoration: 'line-through',
                                        opacity: 0.7
                                      }}>
                                        <Box
                                          onClick={handleCheckboxChange}
                                          sx={{
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid',
                                            borderColor: theme.palette.success.main,
                                            borderRadius: '4px',
                                            backgroundColor: theme.palette.success.main,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '8px',
                                            marginTop: '2px',
                                            '&:hover': {
                                              backgroundColor: theme.palette.success.dark,
                                              transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          <Typography sx={{ 
                                            color: 'white', 
                                            fontSize: '14px', 
                                            lineHeight: 1,
                                            fontWeight: 700
                                          }}>
                                            ✓
                                          </Typography>
                                        </Box>
                                        <span>{taskText}{childrenArray.slice(1)}</span>
                                      </li>
                                    );
                                  }
                                }
                                
                                // Check if parent is OL for numbered list
                                const parent = props.node?.parent;
                                const isNumberedList = parent && parent.tagName === 'ol';
                                
                                if (isNumberedList) {
                                  // For numbered lists, use default browser styling
                                  return (
                                    <li style={{
                                      margin: '4px 0',
                                      lineHeight: '1.5',
                                      color: theme.palette.text.primary,
                                      listStyleType: 'decimal',
                                      listStyle: 'decimal',
                                      display: 'list-item'
                                    }}>
                                      {children}
                                    </li>
                                  );
                                }
                                
                                // Regular bullet list item
                                return (
                                  <li style={{
                                    margin: '4px 0',
                                    lineHeight: '1.5',
                                    color: theme.palette.text.primary,
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                  }}>
                                    <span style={{
                                      marginRight: '8px',
                                      marginTop: '1px',
                                      color: theme.palette.text.primary
                                    }}>•</span>
                                    <span>{children}</span>
                                  </li>
                                );
                              },
                          blockquote: ({ children }) => (
                            <blockquote style={{
                              margin: '16px 0',
                              padding: '12px 16px',
                              borderLeft: `4px solid ${theme.palette.primary.main}`,
                              backgroundColor: alpha(theme.palette.grey[100], 0.5),
                              borderRadius: '0 4px 4px 0',
                              fontStyle: 'italic',
                              color: theme.palette.text.secondary
                            }}>
                              {children}
                            </blockquote>
                          ),
                          table: ({ children }) => (
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              margin: '16px 0',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                            }}>
                              {children}
                            </table>
                          ),
                          thead: ({ children }) => (
                            <thead style={{
                              backgroundColor: alpha(theme.palette.grey[100], 0.8)
                            }}>
                              {children}
                            </thead>
                          ),
                          th: ({ children }) => (
                            <th style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontWeight: 500,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                              backgroundColor: alpha(theme.palette.grey[100], 0.8),
                              color: theme.palette.text.primary
                            }}>
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td style={{
                              padding: '12px',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                              color: theme.palette.text.primary
                            }}>
                              {children}
                            </td>
                          ),
                          hr: () => (
                            <hr style={{
                              margin: '24px 0',
                              border: 'none',
                              height: '2px',
                              backgroundColor: alpha(theme.palette.grey[400], 0.3),
                              borderRadius: '1px'
                            }} />
                          ),
                          strong: ({ children }) => (
                            <strong style={{
                              fontWeight: 700,
                              color: theme.palette.text.primary
                            }}>
                              {children}
                            </strong>
                          ),
                          em: ({ children }) => (
                            <em style={{
                              fontStyle: 'italic',
                              color: theme.palette.text.secondary
                            }}>
                              {children}
                            </em>
                          ),
                          code: ({ inline, className, children, ...props }) => {
                            if (inline) {
                              return (
                                <code
                                  style={{
                                    backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                    fontSize: '0.85em',
                                    color: theme.palette.text.primary
                                  }}
                                  {...props}
                                >
                                  {children}
                                </code>
                              );
                            }
                            return (
                              <pre
                                style={{
                                  backgroundColor: alpha(theme.palette.grey[900], 0.05),
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: '8px',
                                  padding: '16px',
                                  margin: '16px 0',
                                  overflow: 'auto',
                                  fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                  fontSize: '0.9em',
                                  lineHeight: '1.6',
                                  color: theme.palette.text.primary,
                                  whiteSpace: 'pre',
                                  tabSize: 4
                                }}
                              >
                                <code {...props}>{children}</code>
                              </pre>
                            );
                          }
                        }}
                          >
                            {(() => {
                              let content = formData.role_documentation || '*Type to see live preview...*';
                              // Pre-process to fix checkbox syntax
                              content = content.replace(/- \[ \]/g, '- ☐');
                              content = content.replace(/- \[x\]/g, '- ☑');
                              return content;
                            })()}
                          </ReactMarkdown>
                        </Box>
                        </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Conflict Warning */}
            {conflicts.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Schedule Conflicts Detected
                  </Typography>
                  {conflicts.map((conflict, index) => (
                    <Typography key={index} variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                      • {conflict}
                    </Typography>
                  ))}
                </Alert>
              </Grid>
            )}

          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {(task || shift) && canDelete && (
              <Button
                onClick={handleDelete}
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                sx={{ borderRadius: 2 }}
              >
                Delete Task
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancel
            </Button>
            {canEdit && (
              <Button
                onClick={handleSave}
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{ 
                  borderRadius: 2, 
                  px: 3,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                {(task || shift) ? 'Update Task' : 'Create Task'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Employee Selection Dialog */}
      <Dialog
        open={employeeSelectionDialogOpen}
        onClose={handleCloseEmployeeSelectionDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '40vh',
            width: '600px',
            maxWidth: '600px',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Employee
          </Typography>
          <IconButton onClick={handleCloseEmployeeSelectionDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search employees..."
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredEmployeesForSelection().map((employee) => (
                  <TableRow 
                    key={employee.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {employee.first_name} {employee.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.email} • {employee.department?.name || 'No Department'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogEmployeeSelect(employee)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          bgcolor: dialogSelectedEmployee?.id === employee.id ? 'black' : 'transparent',
                          color: dialogSelectedEmployee?.id === employee.id ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: dialogSelectedEmployee?.id === employee.id ? 'black' : 'transparent',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredEmployeesForSelection().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {employeeSearchTerm ? 'No employees found matching your search.' : 'No employees available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary">
            {dialogSelectedEmployee ? `Selected: ${dialogSelectedEmployee.first_name} ${dialogSelectedEmployee.last_name}` : 'No employee selected'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseEmployeeSelectionDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmEmployeeSelection}
              variant="contained"
              disabled={!dialogSelectedEmployee}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: 'black',
                color: 'white',
                border: '1px solid black',
                boxShadow: `0 4px 12px ${alpha('#000', 0.3)}`,
                '&:hover': {
                  background: 'black',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 16px ${alpha('#000', 0.4)}`
                },
                '&:disabled': {
                  background: alpha('#000', 0.6),
                  color: 'white'
                }
              }}
            >
              Select Employee
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Department Selection Dialog */}
      <Dialog
        open={departmentSelectionDialogOpen}
        onClose={handleCloseDepartmentSelectionDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '40vh',
            width: '600px',
            maxWidth: '600px',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Department
          </Typography>
          <IconButton onClick={handleCloseDepartmentSelectionDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search departments..."
              value={departmentSearchTerm}
              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredDepartmentsForSelection().map((department) => (
                  <TableRow 
                    key={department.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {department.name}
                        </Typography>
                        {department.description && (
                          <Typography variant="caption" color="text.secondary">
                            {department.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogDepartmentSelect(department)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          bgcolor: dialogSelectedDepartment?.id === department.id ? 'black' : 'transparent',
                          color: dialogSelectedDepartment?.id === department.id ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: dialogSelectedDepartment?.id === department.id ? 'black' : 'transparent',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredDepartmentsForSelection().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {departmentSearchTerm ? 'No departments found matching your search.' : 'No departments available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary">
            {dialogSelectedDepartment ? `Selected: ${dialogSelectedDepartment.name}` : 'No department selected'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseDepartmentSelectionDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDepartmentSelection}
              variant="contained"
              disabled={!dialogSelectedDepartment}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: 'black',
                color: 'white',
                border: '1px solid black',
                boxShadow: `0 4px 12px ${alpha('#000', 0.3)}`,
                '&:hover': {
                  background: 'black',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 16px ${alpha('#000', 0.4)}`
                },
                '&:disabled': {
                  background: alpha('#000', 0.6),
                  color: 'white'
                }
              }}
            >
              Select Department
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.grey[900], 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500, 
          color: 'error.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
            }}>
              <DeleteIcon sx={{ fontSize: 24, color: 'error.main' }} />
            </Box>
            Delete Task
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this task?
          </Typography>
          {selectedEmployeeData && (
            <Typography variant="body2" color="text.secondary">
              <strong>Employee:</strong> {selectedEmployeeData.first_name} {selectedEmployeeData.last_name}
            </Typography>
          )}
          {formData.shift_date && (
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {formData.shift_date.format('MMMM D, YYYY')}
            </Typography>
          )}
          {formData.start_time && formData.end_time && (
            <Typography variant="body2" color="text.secondary">
              <strong>Time:</strong> {formData.start_time.format(timeFormat === '24hour' ? 'HH:mm' : 'h:mm A')} - {formData.end_time.format(timeFormat === '24hour' ? 'HH:mm' : 'h:mm A')}
            </Typography>
          )}
          <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={cancelDelete}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3,
              fontWeight: 500
            }}
          >
            Delete Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Command Menu */}
      {commandMenuOpen && (
        <Popper
          open={commandMenuOpen}
          anchorEl={commandMenuAnchor}
          placement="bottom-start"
          sx={{ zIndex: 9999 }}
        >
          <ClickAwayListener onClickAway={() => setCommandMenuOpen(false)}>
            <Card sx={{ 
              mt: 1, 
              maxWidth: 300,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
            }}>
              <Box sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                {(() => {
                  // Use the same logic as handleInputChange
                  const cursorPos = roleDocRef?.selectionStart || formData.role_documentation.length;
                  const beforeCursor = formData.role_documentation.substring(0, cursorPos);
                  const lines = beforeCursor.split('\n');
                  const currentLine = lines[lines.length - 1];
                  const searchTerm = currentLine.startsWith('/') ? currentLine.substring(1).toLowerCase() : '';
                  let currentIndex = 0;
                  
                  return commandSections.map((section) => {
                    const sectionCommands = section.commands.filter(cmd => {
                      if (searchTerm === '') return true;
                      
                      const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
                      const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
                      const descMatch = cmd.description.toLowerCase().includes(searchTerm);
                      
                      // Special keyword matching
                      const keywordMatches = {
                        'head': cmd.name.includes('heading'),
                        'header': cmd.name.includes('heading'),
                        'h': cmd.name.includes('heading'),
                        'list': cmd.name.includes('bullet') || cmd.name.includes('number') || cmd.name.includes('checklist'),
                        'table': cmd.name.includes('table') || cmd.name.includes('column'),
                        'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
                        'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
                        'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
                      };
                      
                      const keywordMatch = keywordMatches[searchTerm] || false;
                      
                      return nameMatch || aliasMatch || descMatch || keywordMatch;
                    });
                    
                    if (sectionCommands.length === 0) return null;
                    
                    const sectionItems = [];
                    
                    // Section header
                    sectionItems.push(
                      <Box key={section.title} sx={{ 
                        px: 2, 
                        py: 1, 
                        backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600, 
                          color: theme.palette.primary.main,
                          fontSize: '0.75rem',
                          letterSpacing: '0.5px'
                        }}>
                          {section.title}
                        </Typography>
                      </Box>
                    );
                    
                    // Section commands
                    sectionCommands.forEach((command) => {
                      const isSelected = currentIndex === selectedCommandIndex;
                      sectionItems.push(
                        <List key={command.name}>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => executeCommand(command)}
                            sx={{
                              py: 1.5,
                              px: 2,
                              backgroundColor: isSelected 
                                ? alpha(theme.palette.primary.main, 0.1) 
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                color: theme.palette.text.secondary
                              }}>
                                {command.icon}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                  {command.description.split(' ')[0]} {command.description.split(' ')[1] || ''}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  {command.description}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </List>
                      );
                      currentIndex++;
                    });
                    
                    return sectionItems;
                  }).filter(Boolean).flat();
                })()}
              </Box>
            </Card>
          </ClickAwayListener>
        </Popper>
      )}
    </LocalizationProvider>
  );
};

export default ShiftEditor;